import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight, EyeIcon, EyeOffIcon } from './icons';
import { PriceChart } from './PriceChart';
import { StatRow } from './StatRow';
import { PRESALE_GRADIENT } from './PresaleScreen';
import { IncptIcon } from '../icons/IncptIcon';
import { SendReceiveButtons } from './SendReceiveButtons';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { usePresale } from '../../hooks/usePresale';
import { formatPrice } from '../../lib/presale';
import { formatCompactUsd } from '../../lib/format';
import { useIncptTransfers } from '../../hooks/useIncptTransfers';
import { TransactionRow, incptLedgerToTx, presalePurchaseToTx } from './TransactionRow';
import { HIDDEN_BALANCE, INCPT_COIN_ID, useUiStore } from '../../store/ui';
import {
  decimalToApproxNumber,
  formatDecimalDisplay,
} from '@incpt/shared';

/** Same tabs as the on-chain coin page, and the window each one covers. */
const TIMEFRAMES = ['tf1d', 'tf7d', 'tf1m', 'tf1y', 'tfAll'] as const;
const DAY_MS = 86_400_000;
const WINDOWS = [DAY_MS, 7 * DAY_MS, 30 * DAY_MS, 365 * DAY_MS, Infinity];

const fmtWhole = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

/**
 * INCPT's coin page. It mirrors `CoinInfoScreen` — same chart, same card
 * stack — so the token reads like any other coin. Off-chain P2P send is
 * enabled; swap stays locked until listing.
 */
export function IncptCoinScreen({
  onClose,
  onPresale,
  onSend,
  onReceive,
}: {
  onClose: () => void;
  onPresale: () => void;
  onSend: () => void;
  onReceive: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { impact, selection } = useHaptic();
  const { visible, round, purchases, balance, priceHistory, stats, loading } = usePresale();
  const { transfers: incptTransfers } = useIncptTransfers();
  const balanceHidden = useUiStore((s) => !!s.coinBalanceHidden[INCPT_COIN_ID]);
  const toggleBalanceHidden = useUiStore((s) => s.toggleCoinBalanceHidden);
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();

  const [tf, setTf] = useState(1); // a week, as on the prototype's token page

  // Slice by the selected window; a shorter history simply shows what exists.
  const points = useMemo<[number, number][]>(() => {
    if (priceHistory.length === 0) return [];
    const cutoff = Date.now() - WINDOWS[tf]!;
    const within = priceHistory.filter(([ts]) => ts >= cutoff);
    return within.length >= 2 ? within : priceHistory;
  }, [priceHistory, tf]);

  const up = round.marketChange24h >= 0;

  return (
    <div className="relative flex min-h-screen flex-col bg-surface">
      {/* TOP CARD */}
      <div className="rounded-b-[32px] bg-white px-4 pb-6 pt-[calc(var(--tg-safe-top)_+_0.75rem)] shadow-[0px_4px_24px_rgba(0,0,0,0.05)]">
        {!tgBack && (
          <div className="flex h-10 items-center">
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-black/[0.06] text-ink"
              aria-label="Back"
            >
              <ChevronRight size={18} className="rotate-180 text-ink" />
            </button>
          </div>
        )}

        <div className="mt-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <IncptIcon size={54} />
            <div className="flex flex-col gap-2">
              <span className="text-[16px] font-medium leading-none text-ink">
                {t('presale.coinName')}
              </span>
              <span className="text-[14px] font-medium leading-none text-muted">
                {round.symbol}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <span className="text-[28px] font-medium leading-none tabular-nums text-ink">
            ${formatPrice(round.marketPrice)}
          </span>
          <span
            className="rounded-full px-2.5 py-2 text-[14px] font-medium leading-none"
            style={{
              color: up ? '#69cb8b' : '#ff5a5a',
              background: up ? 'rgba(105,203,139,0.1)' : 'rgba(255,90,90,0.1)',
            }}
          >
            {round.marketChange24h > 0 ? '+' : round.marketChange24h < 0 ? '−' : ''}
            {Math.abs(round.marketChange24h).toFixed(2)}%
          </span>
        </div>

        <div className="relative mt-5">
          <PriceChart points={points} />
          {loading && points.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="h-6 w-6 animate-spin rounded-full border-2 border-ink/15 border-t-ink" />
            </div>
          )}
        </div>

        <div className="mt-5 flex items-center rounded-full bg-[rgba(238,239,244,0.6)] p-1">
          {TIMEFRAMES.map((key, i) => (
            <button
              key={key}
              onClick={() => {
                selection();
                setTf(i);
              }}
              className={`flex-1 rounded-full py-2.5 text-[14px] font-medium transition-colors ${
                tf === i ? 'bg-white text-ink shadow-sm' : 'text-black/[0.56]'
              }`}
            >
              {t(`coin.${key}`)}
            </button>
          ))}
        </div>
      </div>

      {/* BOTTOM CARD */}
      <div className="mt-2.5 flex-1 rounded-t-[32px] bg-white px-4 pb-28 pt-4 shadow-[0px_-4px_24px_rgba(0,0,0,0.04)]">
        {/* Balance — flat, not an accordion: there are no per-address rows to
            expand, since the token has no receiving addresses yet. */}
        <div className="flex h-16 items-center rounded-[24px] bg-white px-4 shadow-[0px_4px_20px_rgba(0,0,0,0.08)]">
          <span className="text-[16px] text-ink">{t('coin.yourBalance')}</span>
          <button
            type="button"
            onClick={() => toggleBalanceHidden(INCPT_COIN_ID)}
            aria-label={balanceHidden ? t('wallet.showBalance') : t('wallet.hideBalance')}
            className="ml-1.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted active:bg-black/[0.05]"
          >
            {balanceHidden ? <EyeOffIcon size={22} /> : <EyeIcon size={22} />}
          </button>
          <span className="ml-auto flex flex-col items-end">
            <span className="text-[16px] font-medium tabular-nums text-ink">
              {balanceHidden
                ? HIDDEN_BALANCE
                : fmtWhole(decimalToApproxNumber(balance) * round.marketPrice)}
            </span>
            <span className="text-[12px] tabular-nums text-muted">
              {balanceHidden
                ? '••••'
                : `${formatDecimalDisplay(balance)} ${round.symbol}`}
            </span>
          </span>
        </div>

        {/* Networks — listing target; off-chain send is available now. */}
        <h2 className="mt-6 text-[16px] font-medium text-ink">{t('coin.network')}</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {round.networks.map((n, i) => (
            <span
              key={n.id}
              className={`rounded-full px-4 py-2.5 text-[14px] font-medium ${
                i === 0 ? 'bg-ink text-white' : 'bg-black/[0.05] text-muted'
              }`}
            >
              {n.label}
            </span>
          ))}
        </div>
        <p className="mt-2.5 text-[12px] leading-[1.5] text-muted">
          {t('presale.swapLocked')}
        </p>

        {/* Presale buy */}
        {visible && round.open && (
          <button
            type="button"
            onClick={() => {
              impact('medium');
              onPresale();
            }}
            className="mt-5 flex h-16 w-full items-center justify-center gap-[11px] rounded-[20px] text-white active:opacity-90"
            style={{
              background: PRESALE_GRADIENT,
              boxShadow: '0 6px 20px rgba(63,168,198,.32), inset 0 1px 1px rgba(255,255,255,.35)',
            }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#fff" className="shrink-0">
              <path d="M12 2.5 13.9 8.6 20 10.5 13.9 12.4 12 18.5 10.1 12.4 4 10.5 10.1 8.6Z" />
              <path d="M18.6 16.2 19.4 18.6 21.8 19.4 19.4 20.2 18.6 22.6 17.8 20.2 15.4 19.4 17.8 18.6Z" />
            </svg>
            <span className="text-left leading-[1.25]">
              <span className="block text-[16px] font-bold tracking-[-0.2px]">
                {t('presale.cta')}
              </span>
              <span className="block text-[11.5px] font-medium opacity-[0.88]">
                {t('presale.ctaSub', {
                  price: formatPrice(round.price),
                  min: `$${round.minAmount.toLocaleString('en-US')}`,
                })}
              </span>
            </span>
          </button>
        )}

        {/* History — ledger (P2P / grants) + purchases, same row chrome. */}
        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('presale.history')}</h2>
        {(() => {
          const rows = [
            ...incptTransfers
              .filter((e) => e.kind !== 'purchase')
              .map((e) => incptLedgerToTx(e, i18n.language, t)),
            ...purchases.map((p) => presalePurchaseToTx(p, i18n.language, t)),
          ].sort((a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0));
          if (rows.length === 0) {
            return (
              <div className="mt-3 rounded-[20px] bg-[#f5f6f8] p-5 text-center text-[13px] text-muted">
                {loading ? t('common.loading') : t('presale.historyEmpty')}
              </div>
            );
          }
          return (
            <div className="mt-3 flex flex-col gap-2.5">
              {rows.map((tx, i) => (
                <TransactionRow key={`incpt-tx-${tx.sortTs}-${i}`} tx={tx} light />
              ))}
            </div>
          );
        })()}

        {/* Statistics — same card and rows as the on-chain coin page. */}
        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('coin.statistics')}</h2>
        <div className="mt-3 rounded-[24px] bg-[#f6f7f8] px-4 py-2.5">
          <StatRow label={t('coin.marketCap')} value={formatCompactUsd(stats.marketCap)} />
          <StatRow label={t('coin.volume')} value={formatCompactUsd(stats.volume24h)} />
          <StatRow label={t('coin.created')} value={String(stats.createdYear)} />
          <StatRow label={t('coin.network')} value={round.networks[0]?.short ?? '—'} />
        </div>

        {/* About */}
        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('coin.aboutTitle')}</h2>
        <div className="mt-3 flex flex-col gap-4 rounded-[24px] bg-[#f6f7f8] p-4 text-[14px] leading-[1.5] text-ink">
          <p>{t('presale.aboutIntro')}</p>
          <p>{t('presale.aboutValue')}</p>
          <p>
            <span className="font-semibold">{t('presale.aboutFormulaLabel')}</span>{' '}
            {t('presale.aboutFormula')}
          </p>
          <div>
            <div className="font-semibold">{t('presale.aboutExampleTitle')}</div>
            <ul className="mt-1 list-inside list-disc">
              <li>{t('presale.aboutExample1')}</li>
              <li>{t('presale.aboutExample2')}</li>
              <li>{t('presale.aboutExample3')}</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Same fixed action bar as CoinInfoScreen. */}
      <div className="fixed inset-x-0 bottom-0 z-20 px-4 pb-5 pt-3">
        <SendReceiveButtons onReceive={onReceive} onSend={onSend} onLight />
      </div>
    </div>
  );
}
